/* Reveals "fits your vehicle" badges. Only Make is REQUIRED to match —
 * Model and Year are checked only if the visitor's saved garage vehicle
 * actually has them. Badges render hidden by default (Liquid), so a JS
 * failure means "never shows" rather than "shows to everyone".
 *
 * MATCH MODE — controlled by data-fit-badge-match-mode on <html> (or any
 * ancestor of the badges, read from the closest one found), set via a
 * theme/section toggle:
 *   "any"   (default) — badge shows once Make matches; Model/Year only
 *           narrow the match if the saved vehicle happens to have them.
 *   "model" — badge only shows once the visitor has picked a specific
 *           Model (Make alone is not enough).
 *
 * All product-side fields are list metafields, so all use membership
 * checks against a "||"-delimited data attribute, not equality.
 *
 * Re-evaluates live whenever the saved vehicle changes on THIS page —
 * garage-widget.js / sticky-vehicle-bar.js dispatch a "garage:vehicle-
 * changed" event after every persistVehicle() call, since the native
 * `storage` event only fires in OTHER tabs, never the one that made the
 * change. Also listens for the native `storage` event for cross-tab
 * updates. Each re-run both shows newly-matching badges AND hides ones
 * that stopped matching — not one-directional.
 */
(() => {
  'use strict';

  const listFrom = (value) => (value || '').split('||').filter(Boolean);

  const getStoredVehicle = () => {
    try { return JSON.parse(localStorage.getItem('garage_vehicle') || 'null'); } catch { return null; }
  };

  const getMatchMode = (badge) => {
    const el = badge.closest('[data-fit-badge-match-mode]') || document.documentElement;
    const mode = el.dataset.fitBadgeMatchMode;
    return mode === 'model' ? 'model' : 'any';
  };

  const matches = (badge, stored) => {
    if (!stored?.make?.value) return false;
    const savedMake = stored.make.value;
    const savedModelId = stored.model?.value || null;
    const savedYear = stored.year?.value ? String(stored.year.value) : null;
    const mode = getMatchMode(badge);

    if (mode === 'model' && !savedModelId) return false;

    const makes = listFrom(badge.dataset.productMakes);
    if (!makes.includes(savedMake)) return false;

    if (savedModelId) {
      const modelIds = listFrom(badge.dataset.productModelIds);
      if (!modelIds.includes(savedModelId)) return false;
    }

    if (savedYear) {
      const years = listFrom(badge.dataset.productYears);
      if (!years.includes(savedYear)) return false;
    }

    return true;
  };

  const reveal = (root = document) => {
    const stored = getStoredVehicle();
    root.querySelectorAll('[data-product-fit-badge]').forEach((badge) => {
      badge.hidden = !matches(badge, stored);
    });
  };

  reveal();

  window.addEventListener('garage:vehicle-changed', () => reveal());
  window.addEventListener('storage', (event) => {
    if (event.key === 'garage_vehicle') reveal();
  });

  // Catches product cards added AFTER initial load (cart drawer
  // recommendations, quick-view modals, infinite scroll, etc.).
  new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      m.addedNodes.forEach((node) => {
        if (node.nodeType === 1) reveal(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();